import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cx } from './cx';

export interface C1FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id?: string;
  label: ReactNode;
  error?: ReactNode;
  /** Shown under the field when there is no error. */
  hint?: ReactNode;
}

/**
 * Floating-label field — same behaviour as the Keycloak `field` macro.
 * Uses `placeholder=" "` so `:placeholder-shown` drives the float.
 */
export const C1Field = forwardRef<HTMLInputElement, C1FieldProps>(function C1Field(
  { id, label, error, hint, className, disabled, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const invalid = Boolean(error);

  return (
    <div>
      <div className={cx('c1-field', invalid && 'c1-field-invalid', className)}>
        <input
          ref={ref}
          id={fieldId}
          className="c1-input"
          placeholder=" "
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={
            [invalid ? errorId : undefined, hint && !invalid ? hintId : undefined]
              .filter(Boolean)
              .join(' ') || undefined
          }
          {...props}
        />
        <label className="c1-label" htmlFor={fieldId}>
          {label}
        </label>
      </div>
      {error ? (
        <p className="c1-field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? (
        <p className="c1-otp-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
